/**
 * NotificationBell — MSR portal in-app notification inbox + Web Push opt-in.
 * Bell with unread badge; dropdown lists the user's alerts (live via Realtime);
 * clicking an alert marks it read and navigates to its link. Footer hosts the push
 * enable/disable control, with the iOS "Add to Home Screen" branch.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellIcon } from "@heroicons/react/24/outline";
import { useUserAlerts } from "../../hooks/useUserAlerts";
import { useToast } from "../ui/Toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  enablePush,
  disablePush,
  currentPushSubscribed,
  isPushSupported,
  isIOS,
  isStandalonePWA,
  getNotificationPreferences,
  setNotificationPreferences,
} from "../../services/notifications.service";
import type { UserAlert } from "../../types/notifications.types";

const timeAgo = (iso: string): string => {
  const d = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (s < 60) {
    return "just now";
  }
  if (s < 3600) {
    return `${Math.floor(s / 60)}m ago`;
  }
  if (s < 86400) {
    return `${Math.floor(s / 3600)}h ago`;
  }
  return new Date(iso).toLocaleDateString();
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inAppRef = useRef(true);
  inAppRef.current = inAppEnabled;

  const { alerts, unread, markRead, markAllRead, reload } = useUserAlerts(
    (a) => {
      // Live toast on a new alert (respects the in-app preference).
      if (inAppRef.current) {
        showToast("info", a.title || "New notification");
      }
    },
  );

  useEffect(() => {
    currentPushSubscribed().then(setPushOn);
    getNotificationPreferences()
      .then((p) => {
        if (p) {
          setInAppEnabled(p.in_app_enabled);
          setPushOn((prev) => prev && p.push_enabled);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  const openAlert = async (a: UserAlert) => {
    if (!a.is_read) {
      markRead(a.id);
    }
    setOpen(false);
    if (a.link_url) {
      navigate(a.link_url);
    }
  };

  const handleEnablePush = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      if (res.ok) {
        setPushOn(true);
        if (user?.id) {
          await setNotificationPreferences(user.id, { push_enabled: true });
        }
        showToast("success", "Push notifications enabled");
      } else if (res.reason === "ios-needs-install") {
        showToast(
          "info",
          "On iPhone/iPad: tap Share → Add to Home Screen, open from there, then enable push.",
        );
      } else if (res.reason === "denied") {
        showToast("warning", "Notifications were blocked in the browser.");
      } else if (res.reason === "unsupported") {
        showToast(
          "warning",
          "This browser doesn't support push notifications.",
        );
      } else {
        showToast("error", "Couldn't enable push notifications.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setBusy(true);
    try {
      await disablePush();
      setPushOn(false);
      if (user?.id) {
        await setNotificationPreferences(user.id, { push_enabled: false });
      }
      showToast("success", "Push notifications disabled");
    } finally {
      setBusy(false);
    }
  };

  const iosNeedsInstall = isIOS() && !isStandalonePWA();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() =>
          setOpen((v) => {
            if (!v) {
              reload();
            }
            return !v;
          })
        }
        className="relative rounded-full p-2 text-gray-600 hover:bg-gray-100"
        aria-label="Notifications"
        title="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-80 max-w-[90vw] rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-semibold text-gray-800">
              Notifications
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead()}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-gray-400">
                No notifications yet.
              </p>
            ) : (
              alerts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => openAlert(a)}
                  className={`flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 ${
                    a.is_read ? "" : "bg-blue-50/50"
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
                      a.is_read ? "bg-transparent" : "bg-blue-500"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-800">
                      {a.title || "Notification"}
                    </span>
                    {a.message && (
                      <span className="block truncate text-xs text-gray-600">
                        {a.message}
                      </span>
                    )}
                    <span className="block text-[11px] text-gray-400">
                      {timeAgo(a.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="border-t px-3 py-2">
            {!isPushSupported() ? (
              <p className="text-xs text-gray-400">
                Push not supported on this browser.
              </p>
            ) : iosNeedsInstall ? (
              <p className="text-xs text-gray-500">
                To get push alerts on iPhone/iPad:{" "}
                <b>Share → Add to Home Screen</b>, open the app from there, then
                enable push.
              </p>
            ) : pushOn ? (
              <button
                type="button"
                disabled={busy}
                onClick={handleDisablePush}
                className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Disable push notifications
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={handleEnablePush}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Enable push notifications
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
